import { Edit, EditNote, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { Box, IconButton, Stack, Table, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import {
  useCreateWorkforceScheduleMutation,
  useGetWorkforceSchedulesQuery,
  WorkforceScheduleModify,
} from '../../store/redux/reducers/workforceScheduleSlice'
import { Project, User, WorkforceSchedule } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { StickyTableLeftCell } from '../../styles/TableStyles'
import AddEmployeeMenuPopup from '../Popup/ProjectDistribution/AddEmployeeMenuPopup'
import AddProjectMenuPopup from '../Popup/ProjectDistribution/AddProjectMenuPopup'
import MonthMenuPopup from '../Popup/ProjectDistribution/MonthMenuPopup'
import EmployeeDistributionHeaderTableCell from './EmployeeDistributionHeaderTableCell'
import { ExtraSmallSquareButton } from '../../styles/ButtonStyles'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsProjectLeader } from '../../store/redux/reducers/currentUserSlice'
import { createErrorMessage } from '../../store/utils'
import LoadingWrapper from '../Loading/LoadingWrapper'
import { VacancyTable } from '../VacancyTable/VacancyTable'
import defaultProps from '../../config/defaultProps'
import { useEditDemandMutation, useGetDemandsQuery } from '../../store/redux/reducers/demandSlice'
import Demand from '../../store/types/models/Demand'
import MonthDistributionTableBody from './MonthDistributionTable'

type DataGroupedByMonth = {
  date: Dayjs
}

const MonthDistributionComponent: React.FC<DataGroupedByMonth> = ({ date }) => {
  const [isEditMode, setIsEditMode] = useState(true)
  const tableContainerRef = useRef<HTMLElement | null>(null)
  const vacancyRef = useRef<HTMLButtonElement>(null)
  const addEmployeeRef = useRef<HTMLButtonElement>(null)
  const firstProjectRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const { t } = useTranslation()
  const [monthData, setMonthData] = useState<WorkforceSchedule[]>([])
  const { leaderId } = useAppSelector(state => state.leaderId)
  const currentUser = useSelector(getCurrentUser)
  const isProjectLeader = useSelector(getIsProjectLeader)

  const [stateEmployees, setStateEmployees] = useState<User[]>([])
  const [stateProjects, setStateProjects] = useState<Project[]>([])
  const dbDate = date.format(`${DateFormat.YearMonth}-01`)
  const { displayNotification } = useNotification()
  const { data: workforceResult, isLoading } = useGetWorkforceSchedulesQuery({
    id: leaderId!!,
    props: {
      date: date.format(DateFormat.YearMonth),
      sortField: 'project.name',
    },
  })

  const [editDemand] = useEditDemandMutation()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const firstCellRef = useRef<HTMLTableCellElement | null>(null)
  const [firstCellWidth, setFirstCellWidth] = useState<number>(0)

  useEffect(() => {
    if (firstCellRef.current) {
      const width = firstCellRef.current.getBoundingClientRect().width
      setFirstCellWidth(width)
    }
  }, [stateEmployees, stateProjects, dbDate])

  useEffect(() => {
    setStateEmployees([])
    setStateProjects([])
  }, [leaderId, workforceResult])

  const [createWorkforce] = useCreateWorkforceScheduleMutation()

  const { data: demandResponse, isLoading: isDemandLoading } = useGetDemandsQuery({
    ...defaultProps,
    startDate: date.format('YYYY-MM-DD'),
    completed: false,
    vacancy: true,
  })

  const [demandResponseContent, setDemandResponseContent] = useState<Demand[]>([])
  useEffect(() => {
    if (demandResponse) {
      setDemandResponseContent(demandResponse.content)

      setStateProjects(prevProjects => {
        const projectsFromDemand = demandResponse.content.map(d => d.project)
        const newProjects = projectsFromDemand.filter(p => !prevProjects.some(sp => sp.id === p.id))
        if (newProjects.length === 0) return prevProjects

        return [...prevProjects, ...newProjects]
      })
    }
  }, [demandResponse])

  const secondColumnLeftSpace = useMemo(() => {
    if (firstCellWidth > 200) return 200
    return firstCellWidth
  }, [firstCellWidth])

  const sortedEmployees = useMemo(() => {
    return [...stateEmployees].sort(
      (a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName)
    )
  }, [stateEmployees])

  useEffect(() => {
    if (workforceResult) {
      setMonthData(workforceResult)
      if (workforceResult.length === 0) {
        return
      }

      setStateEmployees(prevEmployees => {
        const uniqueEmployees = new Map<string, User>()
        prevEmployees.forEach(employee => {
          uniqueEmployees.set(employee.id, employee)
        })
        workforceResult.forEach(workforce => {
          const { id } = workforce.employee
          if (!uniqueEmployees.has(id) && workforce.date === dbDate) {
            uniqueEmployees.set(id, workforce.employee)
          }
        })
        return Array.from(uniqueEmployees.values())
      })

      setStateProjects(prevProjects => {
        const uniqueProjects = new Map<string, Project>()
        prevProjects.forEach(project => {
          uniqueProjects.set(project.id, project)
        })
        workforceResult.forEach(workforce => {
          const { id } = workforce.project
          if (!uniqueProjects.has(id)) {
            uniqueProjects.set(id, workforce.project)
          }
        })
        return Array.from(uniqueProjects.values())
      })
    }
  }, [workforceResult, dbDate])

  const handleResize = useCallback(() => {
    if (tableContainerRef.current) {
      setHasOverflow(checkTableContainerOverflow(tableContainerRef.current))
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  useEffect(() => {
    handleResize()
  }, [stateEmployees, handleResize])

  const checkTableContainerOverflow = (tableContainer: HTMLElement | null): boolean => {
    if (tableContainer) {
      return tableContainer.offsetWidth < tableContainer.scrollWidth
    }

    return false
  }

  const isEditionDisabled = () => {
    return currentUser?.id !== leaderId || !isProjectLeader
  }

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode)
  }

  const scrollOffset = 120

  const scrollHandler = (scrollOffset: number) => {
    const tableContainer = tableContainerRef.current
    if (tableContainer) {
      tableContainer.scrollLeft += scrollOffset
    }
  }

  const handleProjectItemAdd = async (project: Project) => {
    const projectExists = stateProjects.some(p => p.id === project.id)
    if (projectExists) return
    setStateProjects(prev => [...prev, project])

    if (stateEmployees.length === 0) {
      return
    }

    const workforceSchedules: WorkforceScheduleModify[] = stateEmployees.map(employee => {
      return {
        projectId: project.id,
        employeeId: employee.id,
        date: dayjs(dbDate).format(DateFormat.YearMonth),
        percent: employee.employmentPercentage || 100,
        removed: false,
      }
    })

    try {
      await createWorkforce({
        leaderId: leaderId!!,
        workforceSchedules: [...workforceSchedules],
      }).unwrap()
      const newWorkforceSchedules = workforceSchedules.map(ws => {
        return {
          id: '',
          project: project,
          employee: stateEmployees.find(e => e.id === ws.employeeId)!!,
          date: ws.date,
          percent: ws.percent,
          description: '',
          removed: false,
        }
      })
      setMonthData([...monthData, ...newWorkforceSchedules])
      setStateProjects(prev => [...prev, project])
    } catch (error) {
      displayNotification({ message: t('page.projectDistribution.projectAddError'), type: 'error' })
    }
  }

  const handleEmployeeItemAdd = async (employee: User) => {
    const employeeExists = stateEmployees.some(e => e.id === employee.id)
    if (employeeExists) return
    setStateEmployees([...stateEmployees, employee])

    if (stateProjects.length === 0) {
      return
    }

    const workforceSchedules: WorkforceScheduleModify[] = stateProjects.map(project => {
      return {
        projectId: project.id,
        employeeId: employee.id,
        date: dayjs(dbDate).format(DateFormat.YearMonth),
        percent: employee.employmentPercentage || 100,
        removed: false,
      }
    })

    try {
      await createWorkforce({
        leaderId: leaderId!!,
        workforceSchedules: [...workforceSchedules],
      }).unwrap()
      const newWorkforceSchedules = workforceSchedules.map(ws => {
        return {
          id: '',
          project: stateProjects.find(p => p.id === ws.projectId)!!,
          employee: employee,
          date: ws.date,
          percent: ws.percent,
          description: '',
          removed: false,
        }
      })
      setStateEmployees([...stateEmployees, employee])
      setMonthData([...monthData, ...newWorkforceSchedules])
    } catch (error) {
      displayNotification({ message: t('page.projectDistribution.employeeAddError'), type: 'error' })
    }
  }

  const handleEmployeeRemove = async (employee: User) => {
    const removeWorkforceData: WorkforceScheduleModify[] = monthData
      .filter(data => data.employee.id === employee.id && dbDate === data.date)
      .map(data => {
        const removeData: WorkforceScheduleModify = {
          ...data,
          projectId: data.project.id,
          employeeId: data.employee.id,
          date: dayjs(data.date).format(DateFormat.YearMonth),
          removed: true,
        }

        return removeData
      })

    try {
      await createWorkforce({
        leaderId: leaderId!!,
        workforceSchedules: [...removeWorkforceData],
      }).unwrap()
      const updatedEmployees = stateEmployees.filter(e => e.id !== employee.id)
      setStateEmployees(updatedEmployees)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleProjectRemove = async (project: Project) => {
    const updatedProjects = stateProjects.filter(p => p.id !== project.id)
    const demand = demandResponseContent.find(d => d.project.id === project.id)

    try {
      if (demand) {
        await editDemand({
          id: demand.id,
          demand: {
            numberOfPeople: demand.numberOfPeople,
            technologies: demand.technologies,
            projectId: demand.project.id,
            submitterId: demand.submitter.id,
            startDate: dayjs(demand.startDate),
            state: demand.state,
            description: demand.description,
            completed: true,
            version: demand.version,
          },
        }).unwrap()
      }
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('page.demands.alert.demandDeleteError')} ${errorMessage}`, type: 'error' })
      return
    }

    setStateProjects(updatedProjects)

    if (stateEmployees.length === 0) {
      return
    }

    const removeWorkforceData: WorkforceScheduleModify[] = monthData
      .filter(data => data.project.id === project.id && dbDate === data.date)
      .map(data => {
        const removeData: WorkforceScheduleModify = {
          ...data,
          projectId: data.project.id,
          employeeId: data.employee.id,
          date: dayjs(data.date).format(DateFormat.YearMonth),
          removed: true,
        }

        return removeData
      })

    try {
      await createWorkforce({
        leaderId: leaderId!!,
        workforceSchedules: removeWorkforceData,
      }).unwrap()
      const updatedEmployees = stateEmployees.filter(
        employee => !removeWorkforceData.some(data => data.employeeId === employee.id)
      )
      setStateEmployees(updatedEmployees)
      setStateProjects(updatedProjects)
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }
  }

  const rightArrowRef = useRef<HTMLButtonElement | null>(null)

  return (
    <LoadingWrapper isLoading={isLoading}>
      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Box display={'flex'} gap={4} alignItems={'baseline'}>
            <Stack direction="row" gap={1}>
              <Typography component="h2" variant="h4" color="text.highEmphasis" textTransform="capitalize">
                {date.format(DateFormat.MonthName)}
              </Typography>
              <Typography paddingTop={'6px'}>{date.format(DateFormat.Year)}</Typography>
            </Stack>
            <ExtraSmallSquareButton
              aria-label={
                isEditMode
                  ? t('page.projectDistribution.aria.editPercentageButton')
                  : t('page.projectDistribution.aria.editDescriptionButton')
              }
              color="primary"
              onClick={handleEditToggle}
              sx={{ '& svg': { width: 14, height: 14, color: 'primary.mainContrastText' } }}
            >
              {isEditMode ? <Edit /> : <EditNote />}
            </ExtraSmallSquareButton>
          </Box>

          <Box alignSelf="center" pr={rightArrowRef.current ? `${rightArrowRef.current.clientWidth}px` : 0}>
            {!isEditionDisabled() && (
              <MonthMenuPopup
                setEmployees={setStateEmployees}
                setProjects={setStateProjects}
                data={monthData.filter(data => data.date === dbDate)}
                demandData={demandResponseContent}
                month={date}
              />
            )}
          </Box>
        </Box>

        <Box display={'flex'}>
          {!isDownSm && (
            <IconButton
              aria-label={t('page.projectDistribution.aria.scrollLeftButton')}
              onClick={() => scrollHandler(-scrollOffset)}
              sx={{
                alignSelf: 'center',
                mr: '4px',
                visibility: hasOverflow ? 'visible' : 'hidden',
                svg: { color: 'text.mediumEmphasis' },
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
          )}

          <Box
            ref={tableContainerRef}
            sx={{
              width: '100%',
              overflowX: 'auto',
              scrollbarGutter: 'stable',
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '::-webkit-scrollbar-thumb': {
                background: 'primary.main',
              },
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StickyTableLeftCell ref={firstCellRef} sx={{ zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {t('page.projectDistribution.project')}
                      <AddProjectMenuPopup
                        handleItemClick={handleProjectItemAdd}
                        isEditionDisabled={isEditionDisabled()}
                        projectsAdded={stateProjects}
                      />
                    </Box>
                  </StickyTableLeftCell>
                  <StickyTableLeftCell
                    sx={{
                      left: secondColumnLeftSpace,
                      color: 'text.highEmphasis',
                      fontWeight: 700,
                      position: isDownSm ? 'unset' : 'sticky',
                    }}
                  >
                    {t('page.projectDistribution.sum')}
                  </StickyTableLeftCell>
                  {sortedEmployees.map((employee, index) => (
                    <EmployeeDistributionHeaderTableCell
                      key={index}
                      onRemoveIconClick={() => handleEmployeeRemove(employee)}
                      isEditionDisabled={isEditionDisabled()}
                      user={employee}
                    />
                  ))}
                  <StickyTableLeftCell
                    component={'td'}
                    padding="checkbox"
                    sx={{
                      right: 0,
                      borderBottom: '1px background.elevation8',
                      visibility: isEditionDisabled() ? 'hidden' : 'visible',
                    }}
                  >
                    <AddEmployeeMenuPopup
                      handleItemClick={handleEmployeeItemAdd}
                      employeesAdded={stateEmployees}
                      vacancyRef={vacancyRef}
                      addEmployeeRef={addEmployeeRef}
                      isVacancyVisible={stateProjects.length !== 0}
                    />
                  </StickyTableLeftCell>
                </TableRow>
              </TableHead>
              <MonthDistributionTableBody
                isEditionDisabled={isEditionDisabled}
                monthData={monthData}
                dbDate={dbDate}
                leaderId={leaderId}
                date={date}
                stateProjects={stateProjects}
                handleProjectRemove={handleProjectRemove}
                sortedEmployees={sortedEmployees}
                secondColumnLeftSpace={secondColumnLeftSpace}
                demandResponseContent={demandResponseContent}
                isEditMode={isEditMode}
                firstProjectRef={firstProjectRef}
                vacancyRef={vacancyRef}
              />
            </Table>
            <VacancyTable
              month={date}
              stateProjects={stateProjects}
              demandResponse={demandResponseContent}
              isLoading={isDemandLoading}
              employeeAdded={stateEmployees.length > 0}
              isEditionDisabled={isEditionDisabled}
              vacancyRef={vacancyRef}
              addEmployeeRef={addEmployeeRef}
              firstProjectRef={firstProjectRef}
            />
          </Box>
          {!isDownSm && (
            <IconButton
              ref={rightArrowRef}
              aria-label={t('page.projectDistribution.aria.scrollRightButton')}
              onClick={() => scrollHandler(scrollOffset)}
              sx={{ alignSelf: 'center', ml: '4px', visibility: hasOverflow ? 'visible' : 'hidden' }}
            >
              <KeyboardArrowRight />
            </IconButton>
          )}
        </Box>
      </Box>
    </LoadingWrapper>
  )
}

export default MonthDistributionComponent